import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Anchor, ChevronLeft, ChevronRight, Dices, Gift, Headphones, Home, Megaphone,
  Menu, Phone, PlayCircle, Plus, Rocket, Search, ShieldCheck, Target, Ticket,
  Trophy, Wallet, X,
} from 'lucide-react';
import Logo from '../../components/Logo';
import UserDropdown from '../../components/UserDropdown';
import AuthModal from '../../components/AuthModal';
import CallbackModal from '../../components/CallbackModal';
import GameSearch from '../../components/GameSearch';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import ParisFooter from './ParisFooter';
import ParisClock from './ParisClock';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { buildLiveChatConfig, openLiveChat } from '../../components/liveChatConfig';
import { useHiddenCategories } from '../../lib/gameCategories';
import {
  bottomNavIcon, pathOf, splitBottomNav, useBottomNavItems,
} from '../../lib/bottomNav';
import { useTopNavLinks } from '../../lib/topNav';
import { renderSettingText } from '../../lib/textTemplate';
import './paris.css';
import { adminShortcutVisible } from '../../lib/adminDomain';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../../lib/domHooks';

// Referans arayüzün imzası iki katlı üst menüdür: üstte büyük ikonlu ana
// kategoriler (ikon üstte, etiket altta, hepsi BÜYÜK HARF), altında daha ince
// bir şeritte kısayollar. Kategoriler kabuğun kendi listesindedir; kısayollar
// **sabit kodlu değildir**, panelde Footer Yönetimi → "Üst menüde de göster"
// işaretlenen menü grubundan gelir (lib/topNav.js).
const NAV_ITEMS = [
  { to: '/', label: 'Anasayfa', icon: Home, end: true },
  { to: '/spor', label: 'Spor', icon: Trophy, sports: true },
  { to: '/slotlar', label: 'Slot', icon: Dices, category: 'slot' },
  { to: '/canli-casino', label: 'Canlı Casino', icon: PlayCircle, category: 'live-casino' },
  { to: '/crash', label: 'Hızlı Oyunlar', icon: Rocket, category: 'crash' },
  { to: '/promosyonlar', label: 'Promosyonlar', icon: Gift },
  // Çark ve çapa kategori değildir; kendi bayrakları açıkken görünürler ve
  // panelden footer menüsüne de eklenmişlerse buradaki kopya düşer (aşağıda).
  { to: '/cark', label: 'Şans Çarkı', icon: Target, flag: 'wheel_enabled', optional: true },
  { to: '/capa', label: 'Çapa', icon: Anchor, flag: 'anchor_enabled', optional: true },
  // Çekiliş kategori değildir; yayında bir çekiliş varken görünür
  // (``raffle_active`` çalışma anındaki durumdur, bkz. CLAUDE.md).
  { to: '/cekilis', label: 'Çekiliş', icon: Ticket, flag: 'raffle_active', optional: true },
  { to: '/etkinlik', label: 'Tahmin', icon: Target, flag: 'prediction_active', optional: true },
];

const ParisLayout = ({ children }) => {
  const { user, balance, bonusBalance } = useAuth();
  const settings = useSettings();
  // Panel başka bir alan adına taşındıysa kısayol çizilmez: adresi
  // bilmediğimiz için bağlantı kurulamaz ve panelin varlığını
  // duyurmanın da anlamı yoktur (bkz. lib/adminDomain.js).
  const showAdminLink = adminShortcutVisible(settings);
  const nav = useNavigate();
  const location = useLocation();
  // Üstteki hızlı arama kutusundan seçilen oyun doğrudan açılabilsin diye
  // kabuk kendi başlatıcısını taşır.
  const launcher = useGameLauncher();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [noticeClosed, setNoticeClosed] = useState(false);
  const hiddenCats = useHiddenCategories();

  // Kategori barı sayfaya sığıyorsa kaydırma okları gizlenir; mobilde ok yok,
  // orada kaydırılabildiğini uçlardaki solma anlatır.
  const navRef = useRef(null);
  const [navOverflow, setNavOverflow] = useState(false);
  const [navEdges, setNavEdges] = useState({ left: false, right: false });

  const callbackEnabled = settings.callback_enabled !== false;
  // Menüdeki "Canlı Destek" düğmesi widget'ın kendisiyle aynı koşula bağlıdır:
  // panelden kapatıldığında (ya da lisans girilmediğinde) widget hiç yüklenmez.
  const liveChatEnabled = useMemo(
    () => Boolean(buildLiveChatConfig(settings, user)),
    [settings, user],
  );

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reset_token')) {
      setAuthTab('reset');
      setAuthOpen(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.auth !== 'login' || user) return;
    setAuthTab('login');
    setAuthOpen(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, user]);

  // Sayfa değişince mobil çekmece kapanır.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Çekmece açıkken arkadaki sayfa kaymasın.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  // Kategorilerin ardındaki kısayollar footer menüsünden gelir; kapalı bölüme
  // (spor, kategori, şans çarkı, çapa) giden bağlantıları hook'un kendisi ayıklar.
  const extraNav = useTopNavLinks();

  // Aynı adres hem kabuk listesinde hem panelde işaretliyse iki kez çizilirdi;
  // yöneticinin yazdığı etiket kazanır, kabuğun kopyası düşer.
  const shortcutPaths = useMemo(() => new Set(
    extraNav.filter((item) => !item.external).map((item) => pathOf(item.to)),
  ), [extraNav]);

  const visibleNav = useMemo(() => NAV_ITEMS.filter((item) => {
    if (item.sports) return settings.settingsLoaded && settings.sports_enabled !== false;
    if (item.optional && shortcutPaths.has(item.to)) return false;
    // Bayraklar tek tek okunur: ``settings`` nesnesinin tamamını bağımlılığa
    // koymak listeyi her renderda yeniden üretir ve ölçüm efektlerini
    // (bar taşması, aktif madde) boşuna tetiklerdi — context değeri her
    // yoklamada yeni bir nesnedir.
    if (item.flag === 'wheel_enabled') return settings.wheel_enabled === true;
    if (item.flag === 'anchor_enabled') return settings.anchor_enabled === true;
    if (item.flag === 'raffle_active') return settings.raffle_active === true;
    if (item.flag === 'prediction_active') return settings.prediction_active === true;
    return !item.category || !hiddenCats.includes(item.category);
  }), [
    hiddenCats, shortcutPaths, settings.settingsLoaded, settings.sports_enabled,
    settings.wheel_enabled, settings.anchor_enabled, settings.raffle_active, settings.prediction_active,
  ]);

  // Oklar yalnızca bar taşarken anlamlı; genişlik hem pencere boyutuyla hem de
  // görünür kategori sayısıyla değiştiği için ikisi de izlenir.
  const measureNav = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setNavOverflow(max > 1);
    setNavEdges({ left: el.scrollLeft > 4, right: max > 4 && el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    measureNav();
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureNav);
      return () => window.removeEventListener('resize', measureNav);
    }
    const observer = new ResizeObserver(measureNav);
    observer.observe(el);
    // İçerik genişliği yazı tipi yüklenmesiyle de değişebilir.
    Array.from(el.children).forEach((child) => observer.observe(child));
    el.addEventListener('scroll', measureNav, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', measureNav);
    };
  }, [measureNav, visibleNav]);

  // Aktif kategori kaydırma alanının dışında kalabiliyordu; sayfa açıldığında
  // görünür alanın ortasına getirilir. `scrollIntoView` sayfayı da kaydırdığı
  // için yalnızca yatay konum elle ayarlanır.
  useEffect(() => {
    const el = navRef.current;
    const active = el?.querySelector('.paris-nav-item.active');
    if (!el || !active) return;
    const target = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [location.pathname, visibleNav]);

  // Alt bardaki gezinme yuvaları panelden gelir; ortadaki giriş/para yatır
  // düğmesi listeye girmez, oturuma göre kendiliğinden değişir.
  const bottomNav = useBottomNavItems();
  const { left: bottomLeft, right: bottomRight } = splitBottomNav(bottomNav);

  const openAuth = (tab) => { setAuthTab(tab); setAuthOpen(true); };

  // Panelden gelen yuva. "menu" tipi bir adrese gitmez, çekmeceyi açar —
  // mobilde menüye başka giriş olmadığı için panel bu yuvayı silmemeyi
  // ayrıca hatırlatır.
  const renderBottomItem = (item, index) => {
    const Icon = bottomNavIcon(item.icon);
    const key = `${item.type}-${item.url}-${index}`;
    if (item.type === 'menu') {
      return (
        <button
          key={key}
          type="button"
          className={`paris-bottom-item ${drawerOpen ? 'active' : ''}`}
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </button>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.url}
        end={item.url === '/'}
        className={({ isActive }) => `paris-bottom-item ${isActive ? 'active' : ''}`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  // Duyuru bandı panelde tanımlı üst bilgi metninden beslenir.
  const announcement = renderSettingText(settings.top_bar_message || '', settings).trim();

  const scrollNav = (direction) => {
    navRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  // Kategori maddesi: ikon üstte, etiket altta. Panelden gelen kısayollar dış
  // adres de olabilir; o durumda yönlendirici değil düz bağlantı kullanılır.
  const renderNavItem = (item) => (item.external ? (
    <a
      key={item.to}
      href={item.to}
      target={item.newTab ? '_blank' : undefined}
      rel={item.newTab ? 'noreferrer' : undefined}
      className="paris-nav-item"
    >
      <item.icon size={24} />
      <span>{item.label}</span>
    </a>
  ) : (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      target={item.newTab ? '_blank' : undefined}
      className={({ isActive }) => `paris-nav-item ${isActive ? 'active' : ''}`}
    >
      <item.icon size={24} />
      <span>{item.label}</span>
    </NavLink>
  ));

  // Kısayol şeridi (ikinci kat): daha ince, ikon yanda.
  const renderShortcut = (item) => (item.external ? (
    <a
      key={item.to}
      href={item.to}
      target={item.newTab ? '_blank' : undefined}
      rel={item.newTab ? 'noreferrer' : undefined}
      className="paris-subnav-item"
    >
      <item.icon size={14} />
      {item.label}
    </a>
  ) : (
    <NavLink
      key={item.to}
      to={item.to}
      target={item.newTab ? '_blank' : undefined}
      className={({ isActive }) => `paris-subnav-item ${isActive ? 'active' : ''}`}
    >
      <item.icon size={14} />
      {item.label}
    </NavLink>
  ));

  return (
    <div className="paris-shell">
      <div className="paris-header-holder">
        <header id={SHELL_IDS.header} className="paris-header">
          <button
            type="button"
            className="paris-burger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menüyü aç"
          >
            <Menu size={20} />
          </button>

          <Logo />

          <div className="paris-header-links">
            <button type="button" className="paris-header-link" onClick={() => setSearchOpen(true)}>
              <Search size={16} /> Hızlı Ara
            </button>
            <button
              type="button"
              className="paris-header-link"
              onClick={() => nav('/promosyon-kodu')}
            >
              <Ticket size={16} /> Promo Kod
            </button>
          </div>

          <div className="paris-header-actions">
            <ParisClock />
            {user ? (
              <>
                <button
                  type="button"
                  className="paris-btn paris-btn-accent paris-btn-deposit"
                  onClick={() => nav('/hesap/para-yatir')}
                  aria-label="Para Yatır"
                >
                  <Plus size={16} />
                  <span className="paris-btn-text">Para Yatır</span>
                </button>
                {/* Bakiye mobilde de görünür; dar ekranda etiketi CSS gizler. */}
                <div className="paris-balance">
                  <Wallet size={16} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="paris-balance-body">
                    <span className="paris-balance-label">Bakiye</span>
                    <span className="paris-balance-value">{Number(balance).toFixed(2)} ₺</span>
                  </span>
                </div>
                {/* Bonus kutusu ancak geniş ekranda sığar; görünürlüğü CSS yönetir. */}
                <div className="paris-balance paris-balance-bonus">
                  <Gift size={16} style={{ color: 'var(--pusu-gold)' }} />
                  <span className="paris-balance-body">
                    <span className="paris-balance-label">Bonus</span>
                    <span className="paris-balance-value">{Number(bonusBalance).toFixed(2)} ₺</span>
                  </span>
                </div>
                <UserDropdown />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="paris-btn paris-btn-outline"
                  onClick={() => openAuth('login')}
                >
                  Giriş Yap
                </button>
                {settings.registration_open !== false && (
                  // Referans arayüzde kayıt düğmesi beyaz/kırmızı arasında
                  // yanıp söner; dikkat çekmesi bilinçlidir.
                  <button
                    type="button"
                    className="paris-btn paris-btn-register"
                    onClick={() => openAuth('register')}
                  >
                    Üye Ol
                  </button>
                )}
              </>
            )}
          </div>
        </header>

        {/* Birinci kat: ikonlu ana kategoriler. */}
        <nav
          id={SHELL_IDS.nav}
          className={`paris-nav ${navEdges.left ? 'has-left' : ''} ${navEdges.right ? 'has-right' : ''}`}
          aria-label="Kategoriler"
        >
          {navOverflow && (
            <button
              type="button"
              className="paris-nav-arrow"
              onClick={() => scrollNav(-1)}
              aria-label="Sola kaydır"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <div className="paris-nav-scroll" ref={navRef}>
            {visibleNav.map(renderNavItem)}
            {user?.is_admin && showAdminLink && (
              <NavLink to="/admin" className="paris-nav-item">
                <ShieldCheck size={24} />
                <span>Yönetim</span>
              </NavLink>
            )}
          </div>
          {navOverflow && (
            <button
              type="button"
              className="paris-nav-arrow"
              onClick={() => scrollNav(1)}
              aria-label="Sağa kaydır"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </nav>

        {/* İkinci kat: panelden gelen kısayollar. Hiç kısayol yoksa şerit
            çizilmez, yoksa başlığın altında boş bir bant kalırdı. */}
        {extraNav.length > 0 && (
          <nav className="paris-subnav" aria-label="Kısayollar">
            <div className="paris-subnav-scroll">
              {extraNav.map(renderShortcut)}
            </div>
          </nav>
        )}
      </div>

      {announcement && !noticeClosed && (
        <div className="paris-notice">
          <span className="paris-notice-icon"><Megaphone size={15} /></span>
          <p className="paris-notice-text">{announcement}</p>
          <button
            type="button"
            className="paris-notice-close"
            onClick={() => setNoticeClosed(true)}
            aria-label="Duyuruyu kapat"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div id={SHELL_IDS.main} className="paris-content">{children}</div>

      <ParisFooter />

      {/* Mobil çekmece: üst menü dar ekranda yatay sığmıyor, aynı maddeler
          dikey listeye iner. */}
      <aside className={`paris-drawer ${drawerOpen ? 'is-open' : ''}`}>
        <div className="paris-drawer-head">
          <Logo />
          <button
            type="button"
            className="paris-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Menüyü kapat"
          >
            <X size={18} />
          </button>
        </div>
        <div className="paris-drawer-scroll">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `paris-drawer-link ${isActive ? 'active' : ''}`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
              <ChevronRight size={15} className="paris-drawer-arrow" />
            </NavLink>
          ))}
          {extraNav.length > 0 && <div className="paris-drawer-rule" />}
          {extraNav.map((item) => (item.external ? (
            <a
              key={item.to}
              href={item.to}
              target={item.newTab ? '_blank' : undefined}
              rel={item.newTab ? 'noreferrer' : undefined}
              className="paris-drawer-link"
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </a>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              target={item.newTab ? '_blank' : undefined}
              className={({ isActive }) => `paris-drawer-link ${isActive ? 'active' : ''}`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
              <ChevronRight size={15} className="paris-drawer-arrow" />
            </NavLink>
          )))}
          {user?.is_admin && showAdminLink && (
            <>
              <div className="paris-drawer-rule" />
              <NavLink to="/admin" className="paris-drawer-link">
                <ShieldCheck size={18} />
                <span>Yönetim Paneli</span>
              </NavLink>
            </>
          )}
        </div>

        {/* İkisi de kapalıysa çekmece dibinde boş bir şerit kalmasın. */}
        {(callbackEnabled || liveChatEnabled) && (
          <div className="paris-drawer-foot">
            {callbackEnabled && (
              <button
                type="button"
                className="paris-drawer-chip"
                onClick={() => { setDrawerOpen(false); setCallbackOpen(true); }}
              >
                <Phone size={16} />
                <span>Beni Ara</span>
              </button>
            )}
            {liveChatEnabled && (
              <button
                type="button"
                className="paris-drawer-chip"
                onClick={() => {
                  setDrawerOpen(false);
                  openLiveChat();
                }}
              >
                <Headphones size={16} />
                <span>Canlı Destek</span>
              </button>
            )}
          </div>
        )}
      </aside>

      {drawerOpen && (
        <div
          className="paris-drawer-backdrop"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Alt bar beş yuvadır: panelden gelen dört yuva ile ortadaki sabit
          giriş/para yatır düğmesi. */}
      <nav id={SHELL_IDS.bottomBar} className="app-bottom-bar paris-bottom-bar" aria-label="Alt menü">
        {bottomLeft.map(renderBottomItem)}
        {user ? (
          <NavLink to="/hesap/para-yatir" className="paris-bottom-item is-wallet">
            <Wallet size={20} />
            <span>Para Yatır</span>
          </NavLink>
        ) : (
          <button
            type="button"
            className="paris-bottom-item is-wallet"
            onClick={() => openAuth('login')}
          >
            <Wallet size={20} />
            <span>Giriş Yap</span>
          </button>
        )}
        {bottomRight.map(renderBottomItem)}
      </nav>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[70] flex justify-center px-4 pt-24"
          style={{ background: 'rgba(11, 12, 20, 0.8)' }}
          onClick={() => setSearchOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <GameSearch onPlay={(game) => { setSearchOpen(false); launcher.play(game); }} />
          </div>
        </div>
      )}

      <GameChoiceModal
        game={launcher.choice}
        user={launcher.user}
        balance={launcher.balance}
        bonusBalance={launcher.bonusBalance}
        demoAvailable={launcher.demoAvailable}
        onClose={launcher.closeChoice}
        onReal={launcher.startReal}
        onDemo={launcher.startDemo}
        onLogin={() => launcher.openAuth('login')}
        onRegister={() => launcher.openAuth('register')}
      />
      {launcher.activeGame && (
        <GameModal
          key={`${launcher.activeGame.id}-${launcher.mode}`}
          game={launcher.activeGame}
          mode={launcher.mode}
          onClose={launcher.closeGame}
        />
      )}

      <AuthModal
        open={authOpen || !!launcher.authTab}
        onClose={() => { setAuthOpen(false); launcher.setAuthTab(null); }}
        defaultTab={launcher.authTab || authTab}
        registrationOpen={settings.registration_open !== false}
      />
      <CallbackModal open={callbackEnabled && callbackOpen} onClose={() => setCallbackOpen(false)} />
    </div>
  );
};

export default ParisLayout;
